<template>
  <layout :title="$t('legacy.preparation.title')">
    <div slot="layout-content">
      <template v-if="!isLoading">
        <b-col>
          <p>{{ $t('legacy.preparation.modules_intro') }}</p>
          <h2>{{ $t('legacy.preparation.modules') }}</h2>
          <b-card
            v-for="(testModule, key) in modules"
            :key="key"
            no-body
          >
            <b-card-header header-tag="header" role="tab">
              <b-btn
                v-b-toggle="`accordion-${key}`"
                block
                href="#"
                variant="light"
                :class="['text-left', { active: `accordion-${key}` == activeAccordion }]"
              >
                <icon
                  class="icon"
                  name="caret-right"
                  scale="2"
                />
                {{ testModule.label }}
                <template v-if="testModule.isAdvanced">
                  - {{ $t('legacy.preparation.module_advanced') }}
                </template>
              </b-btn>
            </b-card-header>
            <b-collapse
              :id="`accordion-${key}`"
              role="tabpanel"
              accordion="module-accordion"
            >
              <b-card-body>
                <b-row>
                  <template v-if="testModule.sub_modules">
                    <b-col cols="12">
                      <p>{{ $t('legacy.preparation.modules_composite') }}</p>
                    </b-col>

                    <b-col
                      v-for="(subModules, type, idx) in testModule.sub_modules"
                      :key="idx"
                      cols="6"
                      :class="{ 'border-right': idx === 0 }"
                    >
                      <preparation-cards :cards="subModules" :type="type" @change="onCardsChange" />
                    </b-col>
                    <b-col cols="12">
                      <b-button
                        class="mt-2"
                        :disabled="!selectedSubModules.browser || !selectedSubModules.messaging"
                        block
                        @click="onSelectedSubModuleClick"
                      >
                        {{ $t("actions.do_the_test") }}
                      </b-button>
                    </b-col>
                  </template>
                  <template v-else>
                    <b-col v-for="(languages, language, idx) in testModule.tests" :key="idx" cols="12">
                      <h5>{{ $t(`languages.${_.lowerCase(language)}`) }}</h5>
                      <b-row>
                        <b-col
                          v-for="test in languages"
                          :key="test.id"
                          sm="6"
                          md="4"
                          lg="3"
                          class="mb-2"
                        >
                          <preparation-card :item="test" />
                        </b-col>
                        <b-col v-if="idx === 0" cols="12">
                          <hr>
                        </b-col>
                      </b-row>
                    </b-col>
                  </template>
                </b-row>
              </b-card-body>
            </b-collapse>
          </b-card>
        </b-col>
      </template>
      <loader-text
        v-else
        title="dashboard.loading"
      />
      <b-modal
        v-if="$helper.isLegacy && hasOnGoing && $helper.hasPlayerWeb"
        id="modalOnGoing"
        v-model="hasOnGoing"
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        size="xl"
      >
        <template slot="modal-header">
          <div>
            <h5 class="modal-title">
              {{ $t('legacy.preparation.on_going.title') }}
            </h5>
            {{ onGoingTest.test.label }}
          </div>
        </template>
        {{ $t('legacy.preparation.on_going.text') }}
        <template slot="modal-footer">
          <b-col>
            <bt-to-player :career-id="onGoingTest.id" :qs="{player: get().enums.player.prepa, in_progress: true}" is-block>
              {{ $t('legacy.preparation.actions.resume_test') }}
            </bt-to-player>
          </b-col>
          <b-col>
            <form v-loading="`<i class='ti ti-refresh ti-spin'></i>`" @submit.prevent="onModalOnGoingSubmit">
              <b-button type="submit" block>
                {{ $t('legacy.preparation.actions.take_another_test') }}
              </b-button>
            </form>
          </b-col>
          <b-col>
            <form v-loading="`<i class='ti ti-refresh ti-spin'></i>`" @submit.prevent="onModalOnGoingSubmit">
              <b-button :to="{name:'dashboard'}" block>
                {{ $t('legacy.preparation.actions.go_to_dashboard') }}
              </b-button>
            </form>
          </b-col>
        </template>
      </b-modal>
    </div>
  </layout>
</template>

<script>
  /* eslint-disable import/no-extraneous-dependencies */
  import 'vue-awesome/icons/caret-right';
  import 'vue-awesome/icons/caret-down';
  /* eslint-enable import/no-extraneous-dependencies */

  import LoadOnNavigate from '@/mixins/load-on-navigate';

  import BtToPlayer from '@/components/bt-to-player';
  import LoaderText from '@/components/loader-text';
  import PreparationCards from '@/components/preparation-cards';
  import PreparationCard from '@/components/preparation-card';

  import Layout from '@/views/Layout';

  export default {
    name: 'PreparationView',
    components: {
      BtToPlayer,
      Layout,
      LoaderText,
      PreparationCards,
      PreparationCard,
    },
    mixins: [
      LoadOnNavigate,
    ],
    props: {
      setHistory: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        modules: [],
        modulesLabel: {},
        isLoading: null,
        selectedSubModules: {},
        activeAccordion: null,
        hasOnGoing: false,
        onGoingTest: [],
      };
    },
    mounted() {
      this.$root.$on('bv::collapse::state', this.onAccordionStateChange);
    },
    destroyed() {
      this.$root.$off('bv::collapse::state');
    },
    methods: {
      onModalOnGoingSubmit() {
        return this.$ajax.patch('preparations/terminate').then(() => {
          this.onGoingTest = [];
          this.hasOnGoing = false;
        });
      },
      onAccordionStateChange(id, state) {
        if (id === this.activeAccordion || !this.activeAccordion || state) {
          this.activeAccordion = state ? id : null;
        }
      },
      onSelectedSubModuleClick() {
        const compositeModule = this._.find(this.modules, module => module.sub_modules);
        const compositeTest = this._.find(compositeModule.tests, (test) => {
          const { version } = test;
          return version.browser === this.selectedSubModules.browser && version.messaging === this.selectedSubModules.messaging;
        });
        window.location = this.$helper.playerFullUri(compositeTest.id, { player: this.get().enums.player.prepa });
      },
      onCardsChange(data) {
        this.$set(this.selectedSubModules, data.type, data.module);
      },
      async load() {
        if (this.setHistory) {
          window.history.pushState(null, document.title, window.location.href);
        }

        this.isLoading = true;
        const data = await this.$ajax.get('preparations/');
        this.modules = this._.transform(data.modules, (result, value, key) => {
          if (!value.sub_modules) {
            value.tests = this._.groupBy(value.tests, test => test.language);
            value.tests = this._.transform(['FR', 'EN'], (carry, language) => {
              if (this._.get(value.tests, language)) {
                carry[language] = value.tests[language];
              }
              return carry;
            }, {});
          }

          result[key] = value;
          return result;
        });

        this.onGoingTest = this._.first(data.ongoing_prepa);
        this.hasOnGoing = data.ongoing_prepa.length > 0;

        this.isLoading = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '~@/scss/vars';
  @import 'compass/css3/box-shadow';
  @import 'compass/css3/transition';
  @import 'compass/css3/transform';

  .card {
    border: 0;
    border-bottom: 1px solid $primary;

    .card-header {
      padding: 0;
      border: 0;

      .btn {
        border: 0;
        background-color: $white;
        color: $dark-grey;

        &:focus {
          @include box-shadow(none);
        }

        &:hover,
        &.active {
          background-color: $extra-light-grey;
          color: $primary;
        }

        &.active {
          .icon {
            @include transform(rotate(90deg));
          }
        }

        .icon {
          color: $primary;
          font-size: 2rem;
          margin-right: 2rem;

          @include transform(rotate(0deg));
          @include transition(transform 0.2s);
        }
      }
    }

    .card-body {
      background-color: $extra-light-grey;
    }
  }

  /deep/ .modal-backdrop {
    width: unset;
    right: 0;
  }
</style>
